import React, { useState, useRef, useEffect } from "react";
import { PageHeader, Card } from "@/components/Primitives";
import { api, API_BASE, fmtNumber } from "@/lib/api";
import ChartBlock from "@/components/ChartBlock";
import { PaperPlaneTilt, Sparkle, DownloadSimple, ArrowClockwise, ChatCircle, Plus } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Total sales last 7 days",
  "Top 10 SKUs by brand Cartier",
  "Which locations are behind target?",
  "Monthly sales trend 2026 vs 2025",
  "Top 5 salesmen this quarter",
  "Average ticket by location",
];

const Chat = () => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      answer:
        "Welcome to the Morad Yousuf Behbehani analyst. Ask anything about sales performance — I will reply with narrative, charts, tables, and Excel downloads. All figures in KWD.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [history, setHistory] = useState([]);
  const scrollRef = useRef();

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/chat/history?limit=30");
      setHistory(data.rows || []);
    } catch { /* noop */ }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([{
      role: "assistant",
      answer: "New session started. Ask a fresh business question below.",
    }]);
  };

  const send = async (questionOverride) => {
    const question = (questionOverride ?? input).trim();
    if (!question || busy) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/chat/ask", { question, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", ...data }]);
      loadHistory();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", answer: "I couldn't reach the AI service. " + (e?.response?.data?.detail || e.message) }]);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Analyst AI"
        title="Ask the ledger anything"
        subtitle="Natural-language analytics powered by Claude Opus 4.7 · answers include narrative, charts, tables, and downloadable Excel files."
        right={
          <button
            onClick={newSession}
            data-testid="chat-new-session"
            className="no-print text-xs uppercase tracking-[0.22em] border border-myb-200 hover:border-myb-500 hover:text-myb-500 text-muted-foreground px-4 py-2.5 rounded-sm transition-all font-semibold inline-flex items-center gap-2"
          >
            <Plus size={14} /> New conversation
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-[70vh]">
        {/* History sidebar */}
        <Card title="Recent questions" className="lg:col-span-1 !p-0 overflow-hidden flex flex-col max-h-[70vh]">
          <div className="px-5 pt-5 pb-3 text-[10px] uppercase tracking-[0.25em] text-myb-500 font-semibold">History</div>
          <div className="flex-1 overflow-auto scrollbar-thin px-3 pb-5 space-y-1" data-testid="chat-history">
            {history.length === 0 ? (
              <div className="text-xs text-muted-foreground px-2 py-3">No past questions yet.</div>
            ) : history.map((h) => (
              <button
                key={h.id}
                data-testid={`chat-history-${h.id}`}
                onClick={() => { setInput(h.question); }}
                title={h.question}
                className="w-full text-left px-3 py-2 rounded-sm hover:bg-myb-50/70 text-xs text-muted-foreground hover:text-myb-500 truncate transition-all"
              >
                <ChatCircle size={11} weight="duotone" className="inline mr-1.5 text-myb-500" />
                {h.question}
              </button>
            ))}
          </div>
        </Card>

        {/* Conversation */}
        <Card className="lg:col-span-3 !p-0 overflow-hidden flex flex-col max-h-[75vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-5 md:px-7 py-5 space-y-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-sm px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-myb-500 text-white"
                      : "bg-myb-50/60 border hairline"
                  }`}
                >
                  {m.text || m.answer}
                  {m.chart && m.table?.rows?.length > 0 && (
                    <div className="mt-4 -mx-1 bg-white border hairline rounded-sm p-3">
                      <ChartBlock spec={m.chart} data={m.table.rows} height={240} />
                    </div>
                  )}
                  {m.table?.rows?.length > 0 && (
                    <div className="mt-3 max-h-72 overflow-auto border hairline rounded-sm scrollbar-thin bg-white">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-myb-50">
                          <tr>
                            {m.table.columns.map((c) => (
                              <th key={c} className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-myb-500 font-semibold whitespace-nowrap">
                                {c.replace(/_/g, " ")}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {m.table.rows.slice(0, 50).map((r, idx) => (
                            <tr key={idx} className="odd:bg-myb-50/40 hover:bg-myb-50/70">
                              {m.table.columns.map((c) => (
                                <td key={c} className="px-2 py-1.5 border-t hairline whitespace-nowrap">
                                  {typeof r[c] === "number" ? fmtNumber(r[c]) : r[c] ?? "—"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {m.download && (
                    <a
                      data-testid="chat-download-link"
                      href={`${API_BASE.replace(/\/api$/, "")}${m.download.url}`}
                      className="mt-3 inline-flex items-center gap-2 text-xs text-myb-500 hover:text-myb-600 border border-myb-200 px-3 py-1.5 rounded-sm bg-white"
                    >
                      <DownloadSimple size={14} /> {m.download.filename}
                    </a>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-myb-50/60 border hairline rounded-sm px-4 py-3 text-sm">
                  <div className="flex gap-1 items-center">
                    <span className="w-2 h-2 rounded-full bg-myb-500 animate-pulse" />
                    <span className="w-2 h-2 rounded-full bg-myb-500 animate-pulse" style={{ animationDelay: "120ms" }} />
                    <span className="w-2 h-2 rounded-full bg-myb-500 animate-pulse" style={{ animationDelay: "240ms" }} />
                    <span className="ml-2 text-xs text-muted-foreground">Analyzing…</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t hairline px-5 py-3 bg-myb-50/30 flex flex-wrap gap-2" data-testid="chat-suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid={`chat-suggestion-${s.slice(0, 12).replace(/\s/g, "-")}`}
                onClick={() => send(s)}
                disabled={busy}
                className="text-[11px] px-2.5 py-1 rounded-full border hairline bg-white text-muted-foreground hover:border-myb-500 hover:text-myb-500 transition-all"
              >
                {s}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t hairline p-4 flex items-center gap-2 bg-white"
          >
            <Sparkle weight="fill" size={16} className="text-myb-500 mx-1" />
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about sales, brands, locations, salesmen, targets…"
              className="flex-1 bg-background border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground"
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={busy || !input.trim()}
              className="px-4 py-2.5 rounded-sm bg-myb-500 text-white hover:bg-myb-600 disabled:opacity-40 transition inline-flex items-center gap-2"
            >
              <PaperPlaneTilt weight="fill" size={14} />
              <span className="hidden sm:inline text-xs uppercase tracking-wider font-semibold">Ask</span>
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Chat;
